import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { AllCheck, BulkBar, RowCheck, rowKey, useSelection } from "../components/Bulk";
import { errorText, useLoad } from "../hooks";

export function PatientsPage() {
  const { user } = useAuth();
  const list = useLoad(() => api.patients(), []);
  const sites = useLoad(() => api.sites(), []);
  const [filter, setFilter] = useState("");
  const [site, setSite] = useState("");
  const sel = useSelection();
  const isAdmin = user?.role === "admin";
  const canCreate = user?.role === "admin" || user?.role === "operator";
  const f = filter.trim().toLowerCase();
  const rows = (list.data ?? []).filter((p) => (!f || p.study_id.toLowerCase().includes(f) || p.diagnosis.toLowerCase().includes(f))
    && (!site || String(p.site_id) === site));
  const shown = rows.map((p) => rowKey("patients", p.id));

  return (
    <>
      <h1>Patients</h1>
      <div className="card">
        <div className="row">
          <label>Find study ID or diagnosis<input value={filter} onChange={(e) => setFilter(e.target.value)} /></label>
          {(sites.data?.length ?? 0) > 1 && (
            <label>Show site
              <select aria-label="Show site" value={site} onChange={(e) => setSite(e.target.value)}>
                <option value="">all sites</option>
                {(sites.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          )}
        </div>
        {list.error && <div className="error">{list.error}</div>}
        {isAdmin && (
          <BulkBar sel={sel} shown={shown} what="patients" onDeleted={list.reload} presets={[
            { label: "Debug-mode patients", keys: rows.filter((p) => p.debug).map((p) => rowKey("patients", p.id)) },
            { label: "Never tested", keys: rows.filter((p) => !p.last_session_at).map((p) => rowKey("patients", p.id)) },
          ]} />
        )}
        <table style={{ marginTop: "0.8rem" }}>
          <thead><tr>{isAdmin && <th className="pick-col"><AllCheck sel={sel} keys={shown} label="Select all patients shown" /></th>}
            <th>Study ID</th><th>Site</th><th>Diagnosis</th><th>Last session</th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className={sel.has(rowKey("patients", p.id)) ? "picked" : ""}>
                {isAdmin && <td className="pick-col"><RowCheck sel={sel} k={rowKey("patients", p.id)} order={shown} label={`Select ${p.study_id}`} /></td>}
                <td><Link to={`/patients/${p.id}`}>{p.study_id}</Link>{p.debug && <> <span className="badge demo">debug</span></>}</td>
                <td>{sites.data?.find((s) => s.id === p.site_id)?.name ?? p.site_id}</td>
                <td>{p.diagnosis}</td>
                <td>{p.last_session_at ? new Date(p.last_session_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canCreate && <NewPatient siteOptions={sites.data ?? []} needSite={user?.role === "admin"} onDone={list.reload} />}
    </>
  );
}

function NewPatient({ siteOptions, needSite, onDone }: { siteOptions: { id: number; name: string }[]; needSite: boolean; onDone: () => void }) {
  const nav = useNavigate();
  const programs = useLoad(() => api.programs(), []);
  const [f, setF] = useState({ study_id: "", full_name: "", date_of_birth: "", mrn: "", email: "", phone: "", info: "",
    diagnosis: "", site_id: "", program_id: "", serial: "" });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const ident = {
        full_name: f.full_name.trim() || null, date_of_birth: f.date_of_birth || null, mrn: f.mrn || null,
        email: f.email || null, phone: f.phone || null, info: f.info.trim() || null,
      };
      const p = await api.createPatient({
        study_id: f.study_id.trim(),
        site_id: needSite ? Number(f.site_id) || null : null,
        diagnosis: f.diagnosis,
        identifiers: Object.values(ident).some(Boolean) ? ident : null,
        program_id: f.program_id ? Number(f.program_id) : null,
        assigned_monitor_serial: f.serial.trim() || null,
      });
      onDone();
      nav(`/patients/${p.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Add a patient</h2>
      <p className="muted small">Only the study ID is required. Name, date of birth, MRN, contact details and patient
        information are optional; if entered they are encrypted on the server and shown only on the patient's page.
        Any text works as a name (a nickname or a number is fine).</p>
      <form onSubmit={submit} className="row">
        <label>Study ID<input required value={f.study_id} onChange={set("study_id")} pattern="[A-Za-z0-9_-]+" /></label>
        <label>Full name (optional)<input value={f.full_name} onChange={set("full_name")} /></label>
        <label>Date of birth<input type="date" value={f.date_of_birth} onChange={set("date_of_birth")} /></label>
        <label>MRN<input value={f.mrn} onChange={set("mrn")} /></label>
        <label>Email<input type="email" value={f.email} onChange={set("email")} /></label>
        <label>Phone<input value={f.phone} onChange={set("phone")} /></label>
        <label>Diagnosis<input value={f.diagnosis} onChange={set("diagnosis")} placeholder="e.g. RP" /></label>
        <label>Program
          <select aria-label="Program" value={f.program_id} onChange={set("program_id")}>
            <option value="">default program</option>
            {(programs.data ?? []).filter((p) => p.active).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>Monitor serial (if one is sent home)<input value={f.serial} onChange={set("serial")} placeholder="from its EDID" /></label>
        <label style={{ flexBasis: "100%" }}>Patient information
          <textarea rows={2} value={f.info} onChange={set("info")} placeholder="anything the team should know" />
        </label>
        {needSite && (
          <label>Site
            <select aria-label="Site" required value={f.site_id} onChange={set("site_id")}>
              <option value="">choose…</option>
              {siteOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        )}
        <button>Add patient</button>
      </form>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
